/**
 * R7 Änderung der Bemessungsgrundlage (§ 17 Abs. 1 S. 7 UStG): Minderungen, die NACH dem
 * Leistungsmonat erfasst werden, wirken im Erfassungsmonat (MINDERUNG_17, negativ), nie
 * rückwirkend. Nur unter Soll-Besteuerung – unter Ist zahlt der Mieter ohnehin weniger.
 *
 * Dazu die manuellen § 17-Korrekturen aus VatReturn.adjustments:
 * - FORDERUNGSAUSFALL (BOOKING): offener Betrag (oder `value`) negativ im letzten Monat des Zeitraums
 * - NACHTRAG_17 (LINE): signiertes Brutto-Delta (bei Ausgabenzeilen: Vorsteuer-Delta) im letzten Monat
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {createIssue} from '../issues';
import {buildLine, buildSource} from '../line';
import {compareMonthKey, monthKey, monthLabel, monthStart, resolveLeistungsmonat} from '../month';
import {BookingLite, EMPTY_RULE_OUTPUT, RuleContext, RuleOutput, VatAdjustment, VatRule, VatSourceType} from '../types';
import {classifyBooking, classifyPayment, issueIDsOf, linkIssues} from './exclusions';
import {classifyBookingForSoll} from './profile-wechsel';
import {artLabel, openGross, reductionMonthKey} from './soll';

export const buildMinderungLines = (ctx: RuleContext, bookings: BookingLite[]): RuleOutput => {
    const out = EMPTY_RULE_OUTPUT();

    for (const booking of bookings) {
        const c = classifyBooking(ctx, booking);
        if (c.excluded) {
            continue;
        }
        const lm = resolveLeistungsmonat(booking);
        if (!lm || classifyBookingForSoll(ctx.profileAt, monthStart(lm.monthKey)) !== 'SOLL') {
            continue;
        }
        for (const reduction of booking.rentReductionList) {
            const key = reductionMonthKey(reduction);
            if (!key || compareMonthKey(key, lm.monthKey) <= 0) {
                continue;
            }
            const line = buildLine({
                rule: 'MINDERUNG_17', source: c.source, monthKey: key, monthSource: 'DATE',
                category: c.category, rate: c.rate, gross: -Math.abs(reduction.amount),
                label: `Minderung ${artLabel(booking.art)} ${monthLabel(lm.monthKey)} (erfasst ${monthLabel(key)})`,
                idSuffix: reduction.id, issueIDs: issueIDsOf(c.issues),
            });
            out.lines.push(line);
            out.issues.push(...linkIssues(c.issues, line.id));
        }
    }

    return out;
};

const RULES: ReadonlyArray<VatRule> = ['SOLL', 'IST', 'MINDEST_IST', 'MINDEST_IST_ABZUG', 'MINDERUNG_17', 'VERRECHNUNG', 'VORSTEUER', 'KU_ALTUMSATZ', 'ADJUSTMENT', 'AUSGESCHLOSSEN'];

// Längste Regelnamen zuerst, damit MINDEST_IST_ABZUG nicht als MINDEST_IST + Rest gelesen wird
const LINE_ID_PATTERN = new RegExp(`^(BOOKING|PAYMENT|EXPENSE)_(.+)_(${[...RULES].sort((a, b) => b.length - a.length).join('|')})_(\\d{4}-\\d{2})$`);

export const parseLineID = (lineID: string): { type: VatSourceType; sourceID: string; rule: VatRule; monthKey: string } | null => {
    const match = LINE_ID_PATTERN.exec(lineID || '');
    return match ? {type: match[1] as VatSourceType, sourceID: match[2], rule: match[3] as VatRule, monthKey: match[4]} : null;
};

const numberOrNull = (value: string | number | null): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

const invalid = (adjustment: VatAdjustment, text: string) =>
    createIssue('SOURCE_DATA_INVALID', adjustment.id, `Korrektur „${adjustment.reason}“: ${text}`);

export const buildAdjustmentLines = (ctx: RuleContext): RuleOutput => {
    const out = EMPTY_RULE_OUTPUT();
    const targetMonth = monthKey(ctx.range.end);

    for (const adjustment of ctx.adjustments) {
        if (adjustment.action === 'FORDERUNGSAUSFALL' && adjustment.targetType === 'BOOKING') {
            const booking = ctx.bookingsByID.get(adjustment.targetID);
            if (!booking) {
                out.issues.push(invalid(adjustment, `Sollstellung ${adjustment.targetID} nicht gefunden.`));
                continue;
            }
            const c = classifyBooking(ctx, booking);
            if (c.excluded) {
                out.issues.push(invalid(adjustment, `Sollstellung ${adjustment.targetID} ist bereits ausgeschlossen.`));
                continue;
            }
            const explicit = numberOrNull(adjustment.value);
            // Rest nach Minderung: unter SOLL wurde nur amount − Minderungen versteuert (R4 + R7), mehr kann nicht ausfallen
            const open = openGross(booking);
            const amount = explicit !== null ? Math.abs(explicit) : Math.max(0, open);
            const line = buildLine({
                rule: 'ADJUSTMENT', source: c.source, monthKey: targetMonth, monthSource: 'DATE',
                category: c.category, rate: c.rate, gross: -amount,
                label: `Forderungsausfall ${artLabel(booking.art)} ${booking.text ?? ''} – ${adjustment.reason}`.replace(/\s+–/, ' –'),
                idSuffix: adjustment.id, issueIDs: issueIDsOf(c.issues),
            });
            out.lines.push(line);
            out.issues.push(...linkIssues(c.issues, line.id));
            continue;
        }

        if (adjustment.action === 'NACHTRAG_17' && adjustment.targetType === 'LINE') {
            const parsed = parseLineID(adjustment.targetID);
            const delta = numberOrNull(adjustment.value);
            if (!parsed || delta === null) {
                out.issues.push(invalid(adjustment, `Zeile ${adjustment.targetID} oder Betrag ungültig.`));
                continue;
            }
            const sourceID = parsed.sourceID.split(':')[0];

            if (parsed.type === 'BOOKING') {
                const booking = ctx.bookingsByID.get(sourceID);
                if (!booking) {
                    out.issues.push(invalid(adjustment, `Sollstellung ${sourceID} nicht gefunden.`));
                    continue;
                }
                const c = classifyBooking(ctx, booking);
                const line = buildLine({
                    rule: 'ADJUSTMENT', source: c.source, monthKey: targetMonth, monthSource: 'DATE',
                    category: c.category, rate: c.rate, gross: delta,
                    label: `Nachtrag § 17 zu ${artLabel(booking.art)} ${booking.text ?? ''} – ${adjustment.reason}`.replace(/\s+–/, ' –'),
                    idSuffix: adjustment.id, issueIDs: issueIDsOf(c.issues),
                });
                out.lines.push(line);
                out.issues.push(...linkIssues(c.issues, line.id));
                continue;
            }

            if (parsed.type === 'PAYMENT') {
                const payment = ctx.paymentsByID.get(sourceID);
                if (!payment) {
                    out.issues.push(invalid(adjustment, `Zahlung ${sourceID} nicht gefunden.`));
                    continue;
                }
                const c = classifyPayment(ctx, payment);
                const line = buildLine({
                    rule: 'ADJUSTMENT', source: c.source, monthKey: targetMonth, monthSource: 'DATE',
                    category: c.category, rate: c.rate, gross: delta,
                    label: `Nachtrag § 17 zu Zahlung ${payment.text ?? payment.id} – ${adjustment.reason}`,
                    idSuffix: adjustment.id, issueIDs: issueIDsOf(c.issues),
                });
                out.lines.push(line);
                out.issues.push(...linkIssues(c.issues, line.id));
                continue;
            }

            const expenseLite = ctx.expensesByID.get(sourceID);
            if (!expenseLite) {
                out.issues.push(invalid(adjustment, `Ausgabe ${sourceID} nicht gefunden.`));
                continue;
            }
            const source = buildSource('EXPENSE', {id: expenseLite.id, propertyID: expenseLite.propertyID, unitID: null, periodID: null});
            out.lines.push(buildLine({
                rule: 'ADJUSTMENT', source, monthKey: targetMonth, monthSource: 'DATE',
                category: null, rate: 19, gross: delta, tax: delta, kz: '66',
                label: `Nachtrag Vorsteuer zu ${expenseLite.description ?? expenseLite.id} – ${adjustment.reason}`,
                idSuffix: adjustment.id,
            }));
        }
    }

    return out;
};
